/**
 * AnalysisDiff Component
 * 
 * Displays AI-suggested changes as inline word-level diffs (like a git inline diff).
 * Each Keep/Undo action immediately persists to the tickets table via the apply-changes API.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { 
  checkmarkCircleOutline,
  documentTextOutline,
  pricetagOutline,
  createOutline,
  chevronForward,
  chevronDown
} from 'ionicons/icons';
import { computeWordDiff } from '../../../utils/wordDiff';
import DescriptionDiffView from '../DescriptionDiffView/DescriptionDiffView';
import api from '../../../services/api';
import './AnalysisDiff.css';

const FIELD_CONFIG = {
  summary: {
    label: 'Summary',
    icon: createOutline,
    inline: true
  },
  description: {
    label: 'Description',
    icon: documentTextOutline
  },
  labels: {
    label: 'Labels',
    icon: pricetagOutline,
    inline: true
  }
};

async function saveChangesToBackend({ projectId, ticketKey, analysisId, changes }) {
  return await api.post(
    `/projects/${projectId}/tickets/${ticketKey}/apply-changes`,
    {
      analysisId,
      changes: changes.map(c => ({ id: c.id, status: c.status }))
    }
  );
}

function DiffView({ original, suggested, status, reasoning }) {
  const diffOps = useMemo(
    () => (status === 'pending' ? computeWordDiff(original, suggested) : []),
    [original, suggested, status]
  );

  if (status === 'accepted') {
    return (
      <div className="diff-result accepted">
        <span className="diff-text">{suggested || '(empty)'}</span>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="diff-result rejected">
        <span className="diff-text">{original || '(empty)'}</span>
      </div>
    );
  }

  if (!original && !suggested) {
    return <div className="diff-result"><span className="diff-text">(empty)</span></div>;
  }

  return (
    <div className="diff-inline-text">
      <div className="diff-inline-content">
        {diffOps.map((op, idx) => {
          if (op.type === 'equal') {
            return <span key={idx} className="diff-word-equal">{op.value}</span>;
          }
          if (op.type === 'removed') {
            return <span key={idx} className="diff-word-removed">{op.value}</span>;
          }
          if (op.type === 'added') {
            return <span key={idx} className="diff-word-added">{op.value}</span>;
          }
          return null;
        })}
      </div>
      {reasoning && (
        <div className="diff-inline-reasoning">{reasoning}</div>
      )}
    </div>
  );
}

function ChangeItem({ change, onAccept, onReject, disabled, saving }) {
  const config = FIELD_CONFIG[change.field] || { 
    label: change.field, 
    icon: documentTextOutline
  };

  const isPending = change.status === 'pending';
  const isAccepted = change.status === 'accepted';
  const isRejected = change.status === 'rejected';
  const isInline = config.inline;

  const statusActions = (
    <>
      {saving && (
        <div className="change-status saving">
          <IonSpinner name="crescent" />
          <span>Saving…</span>
        </div>
      )}

      {!saving && isPending && (
        <div className="change-actions">
          <button 
            className="action-btn accept"
            onClick={() => onAccept(change.id)}
            disabled={disabled}
            title="Keep this change"
          >
            <span>Keep</span>
          </button>
          <button 
            className="action-btn reject"
            onClick={() => onReject(change.id)}
            disabled={disabled}
            title="Discard this change"
          >
            <span>Undo</span>
          </button>
        </div>
      )}
      
      {!saving && isAccepted && (
        <div className="change-status accepted">
          <IonIcon icon={checkmarkCircleOutline} />
          <span>Saved</span>
        </div>
      )}
      
      {!saving && isRejected && (
        <div className="change-status rejected">
          <span>Dismissed</span>
        </div>
      )}
    </>
  );

  if (isInline) {
    return (
      <div className={`change-item inline ${change.status}${saving ? ' saving' : ''}`}>
        <div className="change-inline-row">
          <span className="change-field-label">{config.label}</span>
          <div className="change-inline-diff">
            <DiffView 
              original={change.original}
              suggested={change.suggested}
              status={change.status}
            />
          </div>
          {statusActions}
        </div>
        {change.reasoning && isPending && (
          <div className="change-inline-reasoning">{change.reasoning}</div>
        )}
      </div>
    );
  }

  const isDescription = change.field === 'description';

  if (isDescription && isPending) {
    return (
      <div className={`change-item ${change.status}${saving ? ' saving' : ''}`}>
        <div className="change-header">
          <div className="change-field">
            <span>{config.label}</span>
          </div>
          {saving && (
            <div className="change-status saving">
              <IonSpinner name="crescent" />
              <span>Saving…</span>
            </div>
          )}
        </div>
        <DescriptionDiffView
          original={change.original}
          suggested={change.suggested}
          changeId={change.id}
          onAccept={onAccept}
          onReject={onReject}
          disabled={disabled}
          reasoning={change.reasoning}
        />
      </div>
    );
  }

  return (
    <div className={`change-item ${change.status}${saving ? ' saving' : ''}`}>
      <div className="change-header">
        <div className="change-field">
          <span>{config.label}</span>
        </div>
        {statusActions}
      </div>

      <DiffView 
        original={change.original}
        suggested={change.suggested}
        status={change.status}
        reasoning={change.reasoning}
      />
    </div>
  );
}

export function AnalysisDiff({ 
  suggestedChanges = [], 
  analysisId,
  projectId,
  ticketKey,
  onApplyComplete,
  disabled = false,
  defaultCollapsed = false
}) {
  const [changes, setChanges] = useState(suggestedChanges);
  const [savingIds, setSavingIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const changesRef = useRef(changes);
  changesRef.current = changes;

  useEffect(() => {
    setChanges(prev => {
      const prevById = new Map(prev.map(c => [c.id, c]));
      return suggestedChanges.map(s => {
        const existing = prevById.get(s.id);
        if (!existing) return s;
        const userActed = existing.status !== 'pending' && s.status === 'pending';
        if (userActed) return existing;
        return { ...existing, original: s.original, status: s.status };
      });
    });
  }, [suggestedChanges]);

  const hasPendingChanges = changes.some(c => c.status === 'pending');
  const hasChanges = changes.length > 0;
  const isSaving = savingIds.size > 0;

  const persistChanges = useCallback(async (updatedChanges, savingChangeIds) => {
    if (!analysisId || !projectId || !ticketKey) {
      setError('Missing required parameters');
      return;
    }

    setError(null);
    setSavingIds(prev => {
      const next = new Set(prev);
      savingChangeIds.forEach(id => next.add(id));
      return next;
    });

    try {
      const result = await saveChangesToBackend({
        projectId,
        ticketKey,
        analysisId,
        changes: updatedChanges
      });

      if (onApplyComplete) {
        onApplyComplete(result);
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      setError(err.message);
      // Revert the failed changes back to pending
      setChanges(prev => prev.map(c =>
        savingChangeIds.includes(c.id) && (c.status === 'accepted' || c.status === 'rejected')
          ? { ...c, status: 'pending' }
          : c
      ));
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        savingChangeIds.forEach(id => next.delete(id));
        return next;
      });
    }
  }, [analysisId, projectId, ticketKey, onApplyComplete]);

  const handleAccept = useCallback((id) => {
    const updated = changesRef.current.map(c => c.id === id ? { ...c, status: 'accepted' } : c);
    setChanges(updated);
    persistChanges(updated, [id]);
  }, [persistChanges]);

  const handleReject = useCallback((id) => {
    const updated = changesRef.current.map(c => c.id === id ? { ...c, status: 'rejected' } : c);
    setChanges(updated);
    persistChanges(updated, [id]);
  }, [persistChanges]);

  const handleAcceptAll = useCallback(() => {
    const pendingIds = changesRef.current.filter(c => c.status === 'pending').map(c => c.id);
    if (pendingIds.length === 0) return;
    const updated = changesRef.current.map(c => c.status === 'pending' ? { ...c, status: 'accepted' } : c);
    setChanges(updated);
    persistChanges(updated, pendingIds);
  }, [persistChanges]);

  const handleRejectAll = useCallback(() => {
    const pendingIds = changesRef.current.filter(c => c.status === 'pending').map(c => c.id);
    if (pendingIds.length === 0) return;
    const updated = changesRef.current.map(c => c.status === 'pending' ? { ...c, status: 'rejected' } : c);
    setChanges(updated);
    persistChanges(updated, pendingIds);
  }, [persistChanges]);

  if (!hasChanges) {
    return null;
  }

  return (
    <div className="analysis-artifacts">
      <div className="artifact-section">
        <div className="artifact-header">
          <div className="artifact-header-left">
            <button
              className="content-collapse-toggle"
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <IonIcon icon={collapsed ? chevronForward : chevronDown} />
            </button>
            <span className="suggested-changes-header-icon" aria-hidden="true">
              <IonIcon icon={createOutline} />
            </span>
            <h3
              className="suggested-changes-title clickable"
              onClick={() => setCollapsed(!collapsed)}
            >
              Suggested Ticket Changes
            </h3>
            <span className="diff-count">{changes.length} changes</span>
          </div>
          <div className="artifact-header-right">
            {hasPendingChanges && (
              <>
                <button 
                  className="bulk-btn accept-all"
                  onClick={handleAcceptAll}
                  disabled={disabled || isSaving}
                >
                  Keep All
                </button>
                <button 
                  className="bulk-btn reject-all"
                  onClick={handleRejectAll}
                  disabled={disabled || isSaving}
                >
                  Undo All
                </button>
              </>
            )}
          </div>
        </div>

        {!collapsed && (
          <div className="analysis-diff-content">

            <div className="changes-list">
              {changes.map(change => (
                <ChangeItem
                  key={change.id}
                  change={change}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  disabled={disabled || isSaving}
                  saving={savingIds.has(change.id)}
                />
              ))}
            </div>

            {error && (
              <div className="diff-error">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalysisDiff;
